import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, Plus } from 'lucide-react'
import { api } from '@frontend/lib/api'
import type { ClientResponse } from '@common/types'
import type { CreateClientInput } from '@common/schemas'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'
import { isHTTPError } from 'ky'

interface AddClientModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

async function createClient(data: CreateClientInput): Promise<ClientResponse> {
  return api.post('clients', { json: data }).json<ClientResponse>()
}

function getTodayDate(): string {
  return new Date().toISOString().split('T')[0]
}

export function AddClientModal({ open, onOpenChange }: AddClientModalProps) {
  const queryClient = useQueryClient()

  // Form state - prepopulate contract start with today's date
  const [name, setName] = useState('')
  const [contractStart, setContractStart] = useState(getTodayDate())
  const [contractEnd, setContractEnd] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [keyExpiration, setKeyExpiration] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setName('')
      setContractStart(getTodayDate())
      setContractEnd('')
      setApiKey('')
      setKeyExpiration('')
      setError(null)
    }
  }, [open])

  const createMutation = useMutation({
    mutationFn: createClient,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients-detailed'] })
      onOpenChange(false)
      toast.success(`Client "${data.client.name}" created`)
    },
    onError: (err) => {
      if (isHTTPError(err)) {
        const errorBody = err.data as { message?: string }
        setError(errorBody.message || 'Failed to create client')
      } else {
        setError('Failed to create client')
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!name.trim()) {
      setError('Name is required')
      return
    }
    if (!contractStart) {
      setError('Contract start date is required')
      return
    }

    createMutation.mutate({
      name: name.trim(),
      contractStart,
      contractEnd: contractEnd || null,
      apiKey: apiKey || null,
      keyExpiration: keyExpiration || null,
    })
  }

  const isSubmitting = createMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Client
          </DialogTitle>
          <DialogDescription>Create a new client in the system.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-1">
              Client Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter client name"
              disabled={isSubmitting}
              autoComplete="off"
              data-1p-ignore
            />
          </div>

          {/* Contract Start */}
          <div>
            <label htmlFor="contractStart" className="block text-sm font-medium mb-1">
              Contract Start Date <span className="text-red-500">*</span>
            </label>
            <input
              id="contractStart"
              type="date"
              value={contractStart}
              onChange={(e) => setContractStart(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting}
            />
          </div>

          {/* Contract End */}
          <div>
            <label htmlFor="contractEnd" className="block text-sm font-medium mb-1">
              Contract End Date
            </label>
            <input
              id="contractEnd"
              type="date"
              value={contractEnd}
              onChange={(e) => setContractEnd(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground mt-1">Leave blank for ongoing contracts</p>
          </div>

          {/* API Key (optional) */}
          <div>
            <label htmlFor="apiKey" className="block text-sm font-medium mb-1">
              Drata API Key
            </label>
            <input
              id="apiKey"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Optional - can add later"
              disabled={isSubmitting}
              autoComplete="off"
              data-1p-ignore
            />
          </div>

          {/* Key Expiration (only if API key is provided) */}
          {apiKey && (
            <div>
              <label htmlFor="keyExpiration" className="block text-sm font-medium mb-1">
                API Key Expiration Date
              </label>
              <input
                id="keyExpiration"
                type="date"
                value={keyExpiration}
                onChange={(e) => setKeyExpiration(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Creating...
                </>
              ) : (
                'Add Client'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
